/* ==========================================================================
   hxp-reels.js — UGC rail + full-screen reel viewer
   --------------------------------------------------------------------------
   Vanilla, no dependencies, loaded `defer` by sections/hxp-ugc-reels.liquid.
   One instance per section, so two of these can coexist on a template.

   WHY THE FILE IS SHAPED THIS WAY

   1. NOTHING IS BUILT UNTIL THE FIRST OPEN.
      The rail is server-rendered HTML plus lazy poster images. The viewer's
      DOM, and every <video> in it, is created on the first card click. A PDP
      that nobody taps pays for this file's parse and nothing else.

   2. AT MOST THREE <video> ELEMENTS EXIST AT ONCE.
      `syncVideos()` keeps one for the active reel and one either side, and
      tears down the rest — `pause()`, drop the source, `load()`. Without the
      `load()` the browser keeps the old buffer and the connection alive, which
      is the usual reason these viewers creep to 400MB on a long session.

   3. LOOPING IS THREE COPIES AND A SILENT RECENTRE.
      With `scroll-snap-type: y mandatory` you cannot fake a wrap by animating
      scrollTop — the snap fights it. Instead the track holds the reels three
      times over and we start in the middle copy. When the settled index
      leaves that copy we add or subtract one copy's height with the scroll
      behaviour forced to `auto`. The pixels under the viewport are identical
      before and after, so there is nothing to see.

   4. THE RECENTRE ONLY RUNS WHEN SCROLLING HAS STOPPED.
      Doing it mid-gesture cancels iOS momentum and feels like the viewer
      stuttered. `scrollend` where it exists, a 120ms debounce where it does
      not.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SUPPORTS_SCROLLEND = 'onscrollend' in window;

  function h(tag, cls, attrs) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    if (attrs) for (var k in attrs) if (attrs[k] != null) el.setAttribute(k, attrs[k]);
    return el;
  }

  /* LUCIDE artwork, verbatim, on Lucide's own 24x24 grid — the same library
     snippets/hxp-icon.liquid uses for the six sibling PDP sections, so the two
     icon sets share a weight and a corner radius instead of merely coexisting.
     Inlined rather than fetched: the theme allows no external icon origin, and
     a <use> sprite cannot receive the fill/stroke override that
     `.hxp-reels__ic` has to apply (see part 0a of hxp-reels.css). */
  var ICON = {
    close: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
    up: '<path d="m18 15-6-6-6 6"/>',
    down: '<path d="m6 9 6 6 6-6"/>',
    bag: '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>' +
         '<path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>',
    share: '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/>' +
           '<circle cx="18" cy="19" r="3"/>' +
           '<line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/>' +
           '<line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>',
    muted: '<path d="M11 4.7a.7.7 0 0 0-1.2-.5L6.4 7.6A1.4 1.4 0 0 1 5.4 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.4a1.4 1.4 0 0 1 1 .4l3.4 3.4a.7.7 0 0 0 1.2-.5z"/>' +
           '<line x1="22" x2="16" y1="9" y2="15"/><line x1="16" x2="22" y1="9" y2="15"/>',
    unmuted: '<path d="M11 4.7a.7.7 0 0 0-1.2-.5L6.4 7.6A1.4 1.4 0 0 1 5.4 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.4a1.4 1.4 0 0 1 1 .4l3.4 3.4a.7.7 0 0 0 1.2-.5z"/>' +
             '<path d="M16 9a5 5 0 0 1 0 6"/><path d="M19.4 18.4a9 9 0 0 0 0-12.8"/>',
    play: '<polygon points="6 3 20 12 6 21 6 3"/>'
  };
  function svg(path, solid) {
    return '<svg class="hxp-reels__ic' + (solid ? ' hxp-reels__ic--solid' : '') +
      '" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + path + '</svg>';
  }
  function iconBtn(cls, name, label) {
    var b = h('button', 'hxp-reels__btn ' + cls, { type: 'button', 'aria-label': label });
    b.innerHTML = svg(ICON[name]);
    return b;
  }

  function Reels(root) {
    this.root = root;
    this.rail = root.querySelector('[data-hxpr-rail]');
    this.opts = JSON.parse(root.getAttribute('data-hxpr-opts') || '{}');

    var payload = root.querySelector('[data-hxpr-data]');
    try { this.reels = JSON.parse(payload.textContent) || []; } catch (e) { this.reels = []; }

    this.n = this.reels.length;
    if (!this.n) return;

    this.loop = !!this.opts.loop && this.n > 1;
    this.copies = this.loop ? 3 : 1;
    this.active = 0;          // real index
    this.domIndex = 0;        // index within the rendered track
    this.muted = true;
    this.built = false;
    this.videos = {};         // domIndex -> <video>
    this.panelOpen = false;
    this.target = null;       // queued nav destination; see go()

    this.bindRail();
  }

  /* ---- rail ------------------------------------------------------------- */
  Reels.prototype.bindRail = function () {
    var self = this;
    if (!this.rail) return;

    this.rail.addEventListener('click', function (e) {
      var card = e.target.closest('[data-hxpr-card]');
      if (!card) return;
      e.preventDefault();
      self.open(parseInt(card.getAttribute('data-hxpr-card'), 10) || 0);
    });

    var prev = this.root.querySelector('[data-hxpr-prev]');
    var next = this.root.querySelector('[data-hxpr-next]');
    if (prev) prev.addEventListener('click', function () { self.nudge(-1); });
    if (next) next.addEventListener('click', function () { self.nudge(1); });
    if (prev || next) {
      this.rail.addEventListener('scroll', this.throttle(function () { self.arrows(); }, 100), { passive: true });
      this.arrows();
    }

    /* Bound unconditionally, because the rail loop depends on it even when the
       merchant has turned the arrows off. Kept on the instance so destroy()
       can detach it — the theme editor replaces the section on every setting
       change, and an anonymous listener here pinned the old instance, its
       observer and its DOM forever. */
    this.onResize = this.throttle(function () {
      self.arrows();
      /* A rail that fits at desktop width usually overflows on a phone, so the
         loop is (re)considered at every width, not only on load. */
      self.setupRailLoop();
    }, 200);
    window.addEventListener('resize', this.onResize);

    /* One frame's grace so flex sizing has resolved — scrollWidth is 0 if this
       runs before the rail has been laid out, and the loop would never arm. */
    requestAnimationFrame(function () { self.setupRailLoop(); });

    if (this.opts.hoverPreview && window.matchMedia('(hover: hover)').matches) this.bindHoverPreview();
  };

  Reels.prototype.nudge = function (dir) {
    var card = this.rail.querySelector('[data-hxpr-card]');
    if (!card) return;
    var step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.rail).columnGap || 0);
    this.rail.scrollBy({ left: step * dir, behavior: REDUCED.matches ? 'auto' : 'smooth' });
  };

  Reels.prototype.arrows = function () {
    var prev = this.root.querySelector('[data-hxpr-prev]');
    var next = this.root.querySelector('[data-hxpr-next]');
    /* A looping rail has no ends, so neither arrow ever dims. */
    if (this.railLoop) {
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
      return;
    }
    var max = this.rail.scrollWidth - this.rail.clientWidth - 2;
    if (prev) prev.disabled = this.rail.scrollLeft <= 2;
    if (next) next.disabled = this.rail.scrollLeft >= max;
  };

  /* ---- infinite rail ----------------------------------------------------
     Same trick as the viewer: three copies, browse the middle one, and shunt
     the scroll position by exactly one copy once it settles outside. The
     pixels either side are identical, so the correction cannot be seen.

     TWO THINGS THIS DELIBERATELY DOES NOT DO

     1. It does not run when the cards already fit. Cloning a four-card rail
        that fits its container turns a centred, static row into a scrolling
        one — it would quietly undo the `safe center` alignment. Overflow is
        the precondition, re-checked on resize because a rail that fits at
        1440px usually does not at 390px.

     2. It does not clone in Liquid. The originals stay the middle copy, so
        they remain the focusable, screen-reader-visible set; the clones are
        `aria-hidden` and out of the tab order. Tripling the markup server-side
        would have tripled what assistive tech reads out.                     */
  Reels.prototype.setupRailLoop = function () {
    if (this.railLoop || !this.opts.loopRail || this.n < 2 || !this.rail) return;
    var rail = this.rail;
    if (rail.scrollWidth <= rail.clientWidth + 4) return;   /* fits — stay centred */

    var originals = Array.prototype.slice.call(rail.children);
    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    originals.forEach(function (el) {
      [before, after].forEach(function (frag) {
        var clone = el.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        frag.appendChild(clone);
      });
    });
    rail.insertBefore(before, originals[0]);
    rail.appendChild(after);

    /* One copy's width, measured rather than computed — padding, gap and the
       negative full-bleed margin all land inside this distance. */
    this.railBlock = originals[0].offsetLeft - rail.children[0].offsetLeft;
    if (this.railBlock <= 0) return;              /* layout not ready; try later */

    this.railLoop = true;
    var prev = rail.style.scrollBehavior;
    rail.style.scrollBehavior = 'auto';
    rail.scrollLeft = this.railBlock;
    void rail.offsetHeight;
    rail.style.scrollBehavior = prev || '';

    var self = this;
    var recentre = function () { self.railSettled(); };
    if (SUPPORTS_SCROLLEND) rail.addEventListener('scrollend', recentre);
    else {
      var t;
      rail.addEventListener('scroll', function () {
        clearTimeout(t); t = setTimeout(recentre, 120);
      }, { passive: true });
    }
    this.arrows();
  };

  Reels.prototype.railSettled = function () {
    if (!this.railLoop) return;
    var b = this.railBlock;
    var x = this.rail.scrollLeft;
    var to = x;
    if (x < b * 0.5) to = x + b;
    else if (x >= b * 2) to = x - b;
    if (to === x) return;
    var prev = this.rail.style.scrollBehavior;
    this.rail.style.scrollBehavior = 'auto';
    this.rail.scrollLeft = to;
    void this.rail.offsetHeight;
    this.rail.style.scrollBehavior = prev || '';
  };

  /* A muted inline clip on hover. Created on first hover and reused, so a
     merchant who leaves this on still ships no video bytes until intent. */
  Reels.prototype.bindHoverPreview = function () {
    var self = this;
    this.rail.addEventListener('pointerenter', function (e) {
      var card = e.target.closest ? e.target.closest('[data-hxpr-card]') : null;
      if (!card) return;
      var i = parseInt(card.getAttribute('data-hxpr-card'), 10);
      var data = self.reels[i];
      if (!data || !data.sources || !data.sources.length) return;
      var v = card.querySelector('.hxp-reels__preview');
      if (!v) {
        v = h('video', 'hxp-reels__preview', { muted: '', playsinline: '', loop: '', preload: 'none' });
        v.muted = true;
        v.src = data.sources[0].url;
        v.addEventListener('playing', function () { v.classList.add('is-ready'); });
        card.appendChild(v);
      }
      v.play().catch(function () {});
    }, true);

    this.rail.addEventListener('pointerleave', function (e) {
      var card = e.target.closest ? e.target.closest('[data-hxpr-card]') : null;
      if (!card) return;
      var v = card.querySelector('.hxp-reels__preview');
      if (v) { v.pause(); v.classList.remove('is-ready'); v.currentTime = 0; }
    }, true);
  };

  Reels.prototype.throttle = function (fn, ms) {
    var t = 0, queued = false;
    return function () {
      var now = Date.now();
      if (now - t >= ms) { t = now; fn(); }
      else if (!queued) { queued = true; setTimeout(function () { queued = false; t = Date.now(); fn(); }, ms - (now - t)); }
    };
  };

  /* ---- viewer construction --------------------------------------------- */
  Reels.prototype.build = function () {
    if (this.built) return;
    var self = this;
    var o = this.opts;

    var modal = h('dialog', 'hxp-reels__modal', { 'aria-label': o.viewerLabel || 'Video gallery' });
    if (o.fit === 'contain') modal.setAttribute('data-fit', 'contain');
    modal.setAttribute('data-panel', 'closed');
    /* Only meaningful at >=990px; the shelf is unconditional below that. */
    modal.setAttribute('data-shelf', o.shelfDesktop === false ? 'off' : 'on');

    var shell = h('div', 'hxp-reels__shell');
    var stage = h('div', 'hxp-reels__stage');
    var track = h('div', 'hxp-reels__track', { tabindex: '-1' });

    /* The track holds `copies * n` reels. Every one carries its real index so
       nothing downstream has to redo the modulo. */
    var total = this.copies * this.n;
    for (var d = 0; d < total; d++) {
      track.appendChild(this.reelEl(d % this.n, d));
    }
    stage.appendChild(track);

    /* progress */
    if (o.showProgress) {
      var prog = h('div', 'hxp-reels__prog');
      for (var p = 0; p < this.n; p++) {
        var seg = h('div', 'hxp-reels__prog-seg');
        seg.appendChild(h('i', 'hxp-reels__prog-fill'));
        prog.appendChild(seg);
      }
      stage.appendChild(prog);
      this.prog = prog;
    }

    /* close + utilities */
    var close = iconBtn('hxp-reels__close', 'close', o.closeLabel || 'Close');
    close.addEventListener('click', function () { self.closeViewer(); });
    stage.appendChild(close);

    var util = h('div', 'hxp-reels__util');
    if (o.showBag) {
      this.bagBtn = iconBtn('', 'bag', o.bagLabel || 'Products in this video');
      this.bagBtn.addEventListener('click', function () { self.togglePanel(); });
      util.appendChild(this.bagBtn);
    }
    if (o.showShare) {
      var share = iconBtn('', 'share', o.shareLabel || 'Share');
      share.addEventListener('click', function () { self.share(); });
      util.appendChild(share);
    }
    this.muteBtn = iconBtn('', 'muted', o.unmuteLabel || 'Unmute');
    /* Stated up front. Set only inside toggleMute(), the button had no pressed
       state at all until someone used it, so it opened announcing nothing. */
    this.muteBtn.setAttribute('aria-pressed', 'false');
    this.muteBtn.addEventListener('click', function () { self.toggleMute(); });
    util.appendChild(this.muteBtn);
    stage.appendChild(util);

    /* prev / next */
    if (o.showArrows) {
      var nav = h('div', 'hxp-reels__nav');
      this.upBtn = iconBtn('', 'up', o.prevLabel || 'Previous video');
      this.downBtn = iconBtn('', 'down', o.nextLabel || 'Next video');
      this.upBtn.addEventListener('click', function () { self.go(-1); });
      this.downBtn.addEventListener('click', function () { self.go(1); });
      nav.appendChild(this.upBtn);
      nav.appendChild(this.downBtn);
      stage.appendChild(nav);
    }

    /* A polite live region. The viewer's primary interaction — moving between
       reels — was completely silent to a screen reader; nothing announced that
       the content had changed. */
    this.live = h('p', 'hxp-reels__live', { role: 'status', 'aria-live': 'polite' });
    stage.appendChild(this.live);

    /* dots */
    if (o.showDots) {
      var dots = h('div', 'hxp-reels__dots');
      for (var q = 0; q < this.n; q++) dots.appendChild(h('i', 'hxp-reels__dot'));
      stage.appendChild(dots);
      this.dots = dots;
    }

    shell.appendChild(stage);

    /* products panel */
    if (o.showBag) {
      var veil = h('div', 'hxp-reels__sheet-veil');
      veil.addEventListener('click', function () { self.togglePanel(false); });
      shell.appendChild(veil);

      var panel = h('aside', 'hxp-reels__panel', { 'aria-label': o.panelTitle || 'Products in this video' });
      panel.appendChild(h('div', 'hxp-reels__panel-grip'));
      var pclose = h('button', 'hxp-reels__panel-close', { type: 'button', 'aria-label': o.closeLabel || 'Close' });
      pclose.innerHTML = svg(ICON.close);
      pclose.addEventListener('click', function () { self.togglePanel(false); });
      panel.appendChild(pclose);
      var head = h('div', 'hxp-reels__panel-head');
      var title = h('h2', 'hxp-reels__panel-title');
      title.textContent = o.panelTitle || 'Products seen in the video';
      head.appendChild(title);
      panel.appendChild(head);
      /* tabindex -1 or the focus() in togglePanel() is a silent no-op and opening
       the panel leaves focus behind on the bag button. */
    this.panelBody = h('div', 'hxp-reels__panel-body', { tabindex: '-1' });
      panel.appendChild(this.panelBody);
      shell.appendChild(panel);
      this.panel = panel;
      this.bindSheetDrag(panel);
    }

    modal.appendChild(shell);
    this.root.appendChild(modal);

    this.modal = modal;
    this.stage = stage;
    this.track = track;
    this.built = true;

    this.bindViewer();
  };

  Reels.prototype.reelEl = function (real, dom) {
    var data = this.reels[real];
    var o = this.opts;
    var el = h('article', 'hxp-reel', { 'data-real': real, 'data-dom': dom });
    if (data.products && data.products.length) el.classList.add('has-products');

    var media = h('div', 'hxp-reel__media');
    if (data.poster) {
      var still = h('img', 'hxp-reel__still', {
        src: data.poster, alt: data.alt || '', loading: 'lazy', decoding: 'async'
      });
      media.appendChild(still);
    }
    el.appendChild(media);
    el.appendChild(h('div', 'hxp-reel__scrim'));

    /* PAUSED AFFORDANCE.
        Every browser can refuse `play()` — a data-saver mode, an iOS Low Power
        setting, a per-site autoplay block. Before this, a refusal left a frozen
        poster with no control anywhere on screen and no way to recover: the tap
        layer only toggled sound, so tapping did nothing visible. The badge is
        the recovery, and it doubles as the pause indicator. */
    var play = h('span', 'hxp-reel__playbadge', { 'aria-hidden': 'true' });
    play.innerHTML = svg(ICON.play, true);
    el.appendChild(play);

    /* Tap the footage: play if it is stopped, otherwise toggle sound. Sound is
        the expected gesture, but it must never be the ONLY thing a tap can do,
        or a refused autoplay is a dead end. */
    var tap = h('button', 'hxp-reel__tap', { type: 'button', 'aria-label': o.unmuteLabel || 'Toggle sound' });
    var self = this;
    tap.addEventListener('click', function () {
      var v = self.videos[parseInt(el.getAttribute('data-dom'), 10)];
      if (v && v.paused) { self.resume(v, el); return; }
      self.toggleMute();
    });
    el.appendChild(tap);

    if (data.caption) {
      var cap = h('p', 'hxp-reel__caption');
      cap.textContent = data.caption;
      el.appendChild(cap);
    }

    if (data.products && data.products.length) {
      var shelf = h('div', 'hxp-reel__shelf');
      data.products.forEach(function (p) { shelf.appendChild(self.shelfCard(p)); });
      el.appendChild(shelf);
    }
    return el;
  };

  Reels.prototype.shelfCard = function (p) {
    var a = h('a', 'hxp-reel__product', { href: p.url });
    if (p.image) a.appendChild(h('img', '', { src: p.image, alt: '', loading: 'lazy', decoding: 'async', width: '46', height: '46' }));
    var info = h('div', 'hxp-reel__pinfo');
    var t = h('p', 'hxp-reel__ptitle'); t.textContent = p.title; info.appendChild(t);
    if (p.price) {
      var pr = h('p', 'hxp-reel__pprice');
      pr.innerHTML = p.price + (p.compare ? ' <s></s>' : '');
      if (p.compare) pr.querySelector('s').textContent = p.compare;
      info.appendChild(pr);
    }
    a.appendChild(info);
    var cta = h('span', 'hxp-reel__pcta');
    cta.textContent = this.opts.ctaLabel || 'Shop';
    a.appendChild(cta);
    return a;
  };

  Reels.prototype.panelCard = function (p) {
    var a = h('a', 'hxp-reels__pcard', { href: p.url });
    var m = h('span', 'hxp-reels__pcard-media');
    if (p.image) m.appendChild(h('img', '', { src: p.imageLg || p.image, alt: '', loading: 'lazy', decoding: 'async' }));
    a.appendChild(m);
    var t = h('span', 'hxp-reels__pcard-title'); t.textContent = p.title; a.appendChild(t);
    var pr = h('span', 'hxp-reels__pcard-price');
    if (p.compare) { var was = h('s', 'hxp-reels__pcard-was'); was.textContent = p.compare; pr.appendChild(was); }
    var now = h('span', 'hxp-reels__pcard-now'); now.textContent = p.price || '';
    pr.appendChild(now);
    a.appendChild(pr);
    var cta = h('span', 'hxp-reels__pcard-cta');
    cta.textContent = this.opts.ctaLabelLg || this.opts.ctaLabel || 'Shop Now';
    a.appendChild(cta);
    return a;
  };

  /* ---- viewer behaviour -------------------------------------------------- */
  Reels.prototype.bindViewer = function () {
    var self = this;

    var settle = function () { self.settled(); };
    if (SUPPORTS_SCROLLEND) {
      this.track.addEventListener('scrollend', settle);
    } else {
      var t;
      this.track.addEventListener('scroll', function () {
        clearTimeout(t); t = setTimeout(settle, 120);
      }, { passive: true });
    }

    /* The active reel is whichever covers most of the track. IntersectionObserver
       reports it without reading layout on every scroll frame. */
    this.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting || en.intersectionRatio < 0.6) return;
        var dom = parseInt(en.target.getAttribute('data-dom'), 10);
        if (dom === self.domIndex) return;
        self.domIndex = dom;
        self.active = parseInt(en.target.getAttribute('data-real'), 10);
        self.onActiveChange();
      });
    }, { root: this.track, threshold: [0.6] });

    Array.prototype.forEach.call(this.track.children, function (el) { self.io.observe(el); });

    this.onKey = function (e) {
      if (!self.modal.open) return;
      /* Never swallow a key that belongs to whatever is focused. Space on a
         focused button is that button's activation, and the panel's scroller
         needs its own arrows — intercepting them at document level made every
         control in the viewer unusable by keyboard. */
      var t = e.target;
      /* `.hxp-reel__tap` is a <button>, so the generic control test swallowed
         Space the moment anyone tapped the footage — after one tap the keyboard
         could no longer pause. It is a full-bleed surface, not a control with
         its own Space semantics, so it is excluded. */
      var onControl = t && t.closest &&
        t.closest('button, a, input, select, textarea, [contenteditable]') &&
        !t.closest('.hxp-reel__tap');
      var inPanel = t && t.closest && t.closest('.hxp-reels__panel');
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        if (inPanel) return;
        e.preventDefault(); self.go(1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        if (inPanel) return;
        e.preventDefault(); self.go(-1);
      } else if (e.key === 'm' || e.key === 'M') {
        if (onControl) return;
        self.toggleMute();
      } else if (e.key === ' ') {
        if (onControl) return;
        e.preventDefault(); self.togglePlay();
      }
    };
    document.addEventListener('keydown', this.onKey);

    /* ESC fires `cancel` on a <dialog>; route it through our own teardown so
       scroll lock and videos are released. */
    this.modal.addEventListener('cancel', function (e) {
      e.preventDefault();
      /* One ESC, one dismissal. Closing the whole viewer out from under someone
         who only meant to put the product sheet away loses their place. */
      if (self.panelOpen) { self.togglePanel(false); return; }
      self.closeViewer();
    });
    this.modal.addEventListener('close', function () { self.teardown(); });
  };

  Reels.prototype.settled = function () {
    this.target = null;          /* the queued nav target has landed */
    if (!this.loop) return;
    var reelH = this.track.clientHeight;
    if (!reelH) return;

    /* Decide on the INDEX, not on a fraction of the scroll height. The old
       0.5/2.5-block thresholds left a gap at the very end of the third copy on
       small reel counts, where the feed could come to rest on the last DOM
       child with nothing below it — an "infinite" scroll with a dead end. */
    var shift = 0;
    if (this.domIndex < this.n) shift = this.n;
    else if (this.domIndex >= this.n * 2) shift = -this.n;
    if (!shift) return;

    /* THE VIDEO MUST SURVIVE THE RECENTRE.
       A jump lands on a different DOM node that happens to show the same reel.
       Left alone, the observer reports a new active element, syncVideos tears
       the current <video> down and builds a fresh one — the clip visibly
       restarts from frame zero every time the loop wraps, which is exactly
       what the loop was supposed to hide. Moving the live element to its twin
       keeps playback, buffer and audio intact; reparenting a <video> does not
       reset it, only touching src/load() does. */
    this.transplant(this.domIndex + shift);
    this.jump(this.track.scrollTop + shift * reelH);
  };

  /* Move every live <video> `shift` reels along, so each stays with the reel it
     belongs to after the recentre. Re-keys the map in one pass. */
  Reels.prototype.transplant = function (newDom) {
    var self = this;
    var shift = newDom - this.domIndex;
    var moved = {};
    Object.keys(this.videos).forEach(function (k) {
      var from = parseInt(k, 10);
      var to = from + shift;
      var target = self.track.children[to];
      var v = self.videos[from];
      if (!target) { self.destroyVideo(from); return; }
      target.querySelector('.hxp-reel__media').appendChild(v);
      moved[to] = v;
    });
    this.videos = moved;
    /* Set BEFORE the scroll so the observer's callback sees the new index and
       short-circuits instead of treating this as a reel change. */
    this.domIndex = newDom;
  };

  Reels.prototype.jump = function (to) {
    var prev = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollTop = to;
    /* Force a reflow so the browser commits the position before the smooth
       behaviour is restored, otherwise the next programmatic scroll animates
       across the whole jump. */
    void this.track.offsetHeight;
    this.track.style.scrollBehavior = prev || '';
  };

  Reels.prototype.onActiveChange = function () {
    this.announce();
    this.syncVideos();
    this.syncDots();
    this.syncPanel();
    this.syncNav();
  };

  Reels.prototype.announce = function () {
    if (!this.live) return;
    var self = this;
    /* Debounced. Holding ArrowDown crosses several reels a second and each
       crossing queued a full polite message, so the reader was still reading
       reel 2 while the user was looking at reel 7. Announcing only where they
       land is both accurate and quieter. */
    clearTimeout(this._announceT);
    this._announceT = setTimeout(function () {
      var d = self.reels[self.active] || {};
      var of_ = self.opts.ofLabel || 'Video {i} of {n}';
      /* Split/join, not String.replace: replace with a string pattern
         substitutes only the FIRST match, and any '$&' in merchant copy would
         be interpreted as a replacement pattern. */
      var txt = of_.split('{i}').join(String(self.active + 1)).split('{n}').join(String(self.n));
      if (d.caption) txt += '. ' + d.caption;
      self.live.textContent = txt;
    }, 350);
  };

  Reels.prototype.syncNav = function () {
    if (!this.upBtn) return;
    if (this.loop) { this.upBtn.disabled = false; this.downBtn.disabled = false; return; }
    this.upBtn.disabled = this.domIndex <= 0;
    this.downBtn.disabled = this.domIndex >= this.track.children.length - 1;
  };

  Reels.prototype.syncDots = function () {
    /* Two independent indicators. They were guarded together once, which meant
       turning dots off silently froze the progress bar. */
    if (this.dots) {
      var kids = this.dots.children;
      for (var i = 0; i < kids.length; i++) kids[i].classList.toggle('is-on', i === this.active);
    }
    if (this.prog) {
      var segs = this.prog.children;
      for (var j = 0; j < segs.length; j++) {
        segs[j].classList.toggle('is-done', j < this.active);
        /* The active segment is reset to empty and then driven by `timeupdate`.
           Leaving it at its previous width shows a stale fill on any reel whose
           video is slow to start or is refused autoplay. */
        segs[j].firstChild.style.width = j < this.active ? '100%' : '0%';
      }
    }
  };

  /* Keep a <video> for the active reel and its neighbours; destroy the rest. */
  Reels.prototype.syncVideos = function () {
    var self = this;
    var keep = {};
    [-1, 0, 1].forEach(function (d) {
      var i = self.domIndex + d;
      if (i < 0 || i >= self.track.children.length) return;
      keep[i] = true;
      self.ensureVideo(i);
    });
    Object.keys(this.videos).forEach(function (k) {
      if (!keep[k]) self.destroyVideo(parseInt(k, 10));
    });

    /* Everything except the active reel is taken out of the tab order. Without
       this the viewer has 3n reels live at once, each with a tap button and a
       product shelf, so reaching Close from the first reel is 12-24 tabs and
       every one of them scrolls the feed. `inert` also hides them from screen
       readers, which is what stops the whole feed being read as one page. */
    var kids = this.track.children;
    for (var i = 0; i < kids.length; i++) {
      kids[i].inert = (i !== this.domIndex);
      /* Only the active reel may carry the badge. */
      if (i !== this.domIndex) kids[i].classList.remove('is-paused');
    }

    this.playActive();
  };

  Reels.prototype.destroyVideo = function (dom) {
    var v = this.videos[dom];
    if (!v) return;
    v.pause();
    v.removeAttribute('src');
    while (v.firstChild) v.removeChild(v.firstChild);
    v.load();                         // releases the buffer + the connection
    if (v.parentNode) v.parentNode.removeChild(v);
    delete this.videos[dom];
  };

  Reels.prototype.ensureVideo = function (dom) {
    if (this.videos[dom]) return this.videos[dom];
    var el = this.track.children[dom];
    if (!el) return null;
    var data = this.reels[parseInt(el.getAttribute('data-real'), 10)];
    if (!data || !data.sources || !data.sources.length) return null;

    /* `metadata` for neighbours, `auto` only once a reel becomes active (set in
       playActive). Preloading two whole clips either side of the one being
       watched is three simultaneous downloads on a phone connection for two
       videos that may never be seen. */
    var v = h('video', 'hxp-reel__video', {
      playsinline: '', 'webkit-playsinline': '',
      preload: dom === this.domIndex ? 'auto' : 'metadata',
      poster: data.poster || null
    });
    v.muted = this.muted;               // property, not attribute — iOS reads this
    v.loop = !!this.opts.loopVideo;
    v.setAttribute('playsinline', '');
    data.sources.forEach(function (s) {
      v.appendChild(h('source', '', { src: s.url, type: s.mime || null }));
    });

    /* Captions. UGC is speech-heavy and was completely inaccessible without a
       track — WCAG 1.2.2. `crossorigin` is required or a VTT served from
       another origin is fetched but silently discarded by the text-track
       algorithm. */
    if (data.captions) {
      /* crossorigin changes how the VIDEO is fetched too, and a CDN that does
         not send Access-Control-Allow-Origin then fails the whole load. The
         section accepts any direct .mp4 URL, so setting it unconditionally
         could black out playback on a reel whose only sin was adding captions.
         It is only actually required for a cross-origin track. */
      var xo = false;
      try { xo = new URL(data.captions, location.href).origin !== location.origin; }
      catch (e) { xo = false; }
      if (xo) v.setAttribute('crossorigin', 'anonymous');
      v.appendChild(h('track', '', {
        kind: 'captions',
        src: data.captions,
        srclang: data.captionsLang || 'en',
        label: data.captionsLabel || 'Captions',
        default: ''
      }));
    }

    var self = this;

    /* ⚠️ NEVER CLOSE OVER `el` IN A LISTENER.
       `el` is the reel this video was BUILT in, and transplant() moves the video
       into its twin on every loop recentre. A captured `el` is therefore stale
       from the first wrap onwards, and the damage is silent:
         · the paused badge lands on a reel three positions off screen while the
           reel the user is actually looking at freezes with no badge at all —
           exactly the dead end this whole fix exists to remove;
         · the progress bar's guard compares a stale data-dom to domIndex, never
           matches again, and the bar stops moving after the first wrap;
         · auto-advance's guard fails the same way and never fires again.
       `host()` reads the CURRENT parent instead, so it survives any number of
       transplants. */
    var host = function () { return v.closest('.hxp-reel'); };
    var hostDom = function () {
      var r = host();
      return r ? parseInt(r.getAttribute('data-dom'), 10) : -1;
    };

    /* The badge is aria-hidden decoration, so the tap layer is the only thing a
       screen reader can perceive here — its label has to say what activating it
       will actually DO. Left permanently on "Unmute", a refused autoplay was
       both invisible and unrecoverable for a non-sighted user. */
    var relabel = function (r, paused) {
      if (!r) return;
      var t = r.querySelector('.hxp-reel__tap');
      if (t) t.setAttribute('aria-label', paused
        ? (self.opts.playLabel || 'Play video')
        : (self.opts.unmuteLabel || 'Toggle sound'));
    };

    v.addEventListener('play', function () {
      var r = host();
      if (r) { r.classList.remove('is-paused'); relabel(r, false); }
    });
    v.addEventListener('pause', function () {
      /* GATED ON BEING THE ACTIVE REEL. Deactivation, recycling and teardown all
         pause too; badging those paints a play button over reels the viewer is
         merely scrolling past or has already left. */
      var r = host();
      if (r && parseInt(r.getAttribute('data-dom'), 10) === self.domIndex) {
        r.classList.add('is-paused');
        relabel(r, true);
        /* Say it as well as show it. */
        if (self.live) self.live.textContent = self.opts.pausedLabel || 'Video paused';
      }
    });
    if (this.prog) {
      v.addEventListener('timeupdate', function () {
        if (hostDom() !== self.domIndex) return;
        var seg = self.prog.children[self.active];
        if (seg && v.duration) seg.firstChild.style.width = (v.currentTime / v.duration * 100) + '%';
      });
    }
    /* When looping is off, rolling off the end advances like a story. */
    if (!this.opts.loopVideo && this.opts.autoAdvance) {
      v.addEventListener('ended', function () {
        if (hostDom() === self.domIndex) self.go(1);
      });
    }

    el.querySelector('.hxp-reel__media').appendChild(v);
    this.videos[dom] = v;
    return v;
  };

  Reels.prototype.playActive = function () {
    var self = this;
    Object.keys(this.videos).forEach(function (k) {
      var v = self.videos[k];
      var el = self.track.children[parseInt(k, 10)];
      if (parseInt(k, 10) === self.domIndex) {
        v.muted = self.muted;
        /* Neighbours were also preloading the whole file; only what is on
            screen deserves `auto`. */
        if (v.preload !== 'auto') v.preload = 'auto';
        var pr = v.play();
        if (pr && pr.catch) pr.catch(function () { if (el) el.classList.add('is-paused'); });
      } else {
        /* Demote as well as promote. Left at 'auto' the outgoing reel carries on
           downloading its whole file in the background for the rest of the
           session. */
        if (v.preload !== 'metadata') v.preload = 'metadata';
        if (!v.paused) v.pause();
      }
    });
  };

  /* Shared by the tap layer and the Space key. A click IS a user gesture, so a
     play() that autoplay policy refused a moment ago will now be granted. */
  Reels.prototype.resume = function (v, el) {
    var pr = v.play();
    if (pr && pr.then) pr.then(function () { if (el) el.classList.remove('is-paused'); })
                        .catch(function () { if (el) el.classList.add('is-paused'); });
  };

  Reels.prototype.togglePlay = function () {
    var v = this.videos[this.domIndex];
    var el = this.track.children[this.domIndex];
    if (!v) return;
    if (v.paused) this.resume(v, el);
    else { v.pause(); if (el) el.classList.add('is-paused'); }
  };

  Reels.prototype.toggleMute = function () {
    this.muted = !this.muted;
    var self = this;
    Object.keys(this.videos).forEach(function (k) { self.videos[k].muted = self.muted; });
    if (this.muteBtn) {
      this.muteBtn.innerHTML = svg(this.muted ? ICON.muted : ICON.unmuted);
      this.muteBtn.setAttribute('aria-label',
        this.muted ? (this.opts.unmuteLabel || 'Unmute') : (this.opts.muteLabel || 'Mute'));
      this.muteBtn.setAttribute('aria-pressed', String(!this.muted));
    }
    /* Unmuting is the gesture that also satisfies autoplay policy, so retry. */
    if (!this.muted) this.playActive();
  };

  Reels.prototype.go = function (dir) {
    /* Advance from the QUEUED position, not the observed one. `domIndex` only
       updates once the observer fires, which is after the smooth scroll ends —
       so three quick taps on the chevron, or an ArrowDown key-repeat, all
       computed the same destination and moved exactly one reel. */
    var from = this.target == null ? this.domIndex : this.target;
    var last = this.track.children.length - 1;
    var target = from + dir;
    if (target < 0 || target > last) {
      if (!this.loop) return;
      this.settled();                       /* recentre, then re-derive */
      from = this.target == null ? this.domIndex : this.target;
      target = from + dir;
      if (target < 0 || target > last) return;
    }
    this.target = target;
    this.track.scrollTo({
      top: target * this.track.clientHeight,
      behavior: REDUCED.matches ? 'auto' : 'smooth'
    });
  };

  /* ---- products panel ---------------------------------------------------- */
  Reels.prototype.syncPanel = function () {
    if (!this.panelBody) return;
    var data = this.reels[this.active];
    var products = (data && data.products) || [];

    if (this.bagBtn) {
      this.bagBtn.hidden = products.length === 0;
      this.bagBtn.setAttribute('aria-expanded', String(this.panelOpen && products.length > 0));
    }
    /* A reel with no products cannot leave the panel open behind it. */
    if (!products.length && this.panelOpen) this.togglePanel(false);

    this.panelBody.textContent = '';
    if (!products.length) {
      var empty = h('p', 'hxp-reels__panel-empty');
      empty.textContent = this.opts.emptyLabel || 'No products tagged in this video.';
      this.panelBody.appendChild(empty);
      return;
    }
    var self = this;
    products.forEach(function (p) { self.panelBody.appendChild(self.panelCard(p)); });
    this.panelBody.scrollTop = 0;
  };

  Reels.prototype.togglePanel = function (force) {
    var want = typeof force === 'boolean' ? force : !this.panelOpen;
    var data = this.reels[this.active];
    if (want && (!data || !data.products || !data.products.length)) return;
    this.panelOpen = want;
    this.modal.setAttribute('data-panel', want ? 'open' : 'closed');
    if (this.bagBtn) this.bagBtn.setAttribute('aria-expanded', String(want));
    if (want && this.panelBody) this.panelBody.focus && this.panelBody.focus({ preventScroll: true });
  };

  /* SWIPE THE SHEET AWAY.
     The grip was drawn but did nothing — a handle that says "drag me" and then
     ignores the drag is worse than no handle. Only the sheet layout gets this:
     above 990px the panel is a static column and there is nothing to dismiss.

     The drag starts only on the grip or the header, never on the scrolling
     product grid, or flicking through products would drag the sheet instead of
     scrolling it. */
  Reels.prototype.bindSheetDrag = function (panel) {
    var self = this, y0 = null, dy = 0, pid = null;
    var isSheet = function () { return !window.matchMedia('(min-width: 990px)').matches; };

    panel.addEventListener('pointerdown', function (e) {
      if (!isSheet()) return;
      if (!e.target.closest('.hxp-reels__panel-grip, .hxp-reels__panel-head')) return;
      if (pid !== null) return;            /* a drag already owns the sheet */
      pid = e.pointerId;
      y0 = e.clientY; dy = 0;
      panel.style.transition = 'none';
      panel.setPointerCapture(e.pointerId);
    });

    panel.addEventListener('pointermove', function (e) {
      /* Only the finger that started the drag moves the sheet. A second finger
         landing mid-gesture used to overwrite y0 and could fling it shut. */
      if (y0 === null || e.pointerId !== pid) return;
      dy = Math.max(0, e.clientY - y0);        /* downward only */
      panel.style.transform = 'translateY(' + dy + 'px)';
    });

    var end = function (e) {
      if (y0 === null || (e && e.pointerId !== pid)) return;
      panel.style.transition = '';
      panel.style.transform = '';
      /* A third of the sheet dismisses it. (A velocity-based flick was described
         in an earlier comment but never implemented — the threshold is the whole
         rule, so the comment now matches the code.) */
      if (dy > panel.offsetHeight * 0.33) self.togglePanel(false);
      y0 = null; dy = 0; pid = null;
    };
    panel.addEventListener('pointerup', end);
    panel.addEventListener('pointercancel', end);
  };

  Reels.prototype.share = function () {
    var data = this.reels[this.active];
    var url = (data && data.shareUrl) || window.location.href;
    if (navigator.share) {
      navigator.share({ title: document.title, url: url }).catch(function () {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(function () {});
    }
  };

  /* ---- open / close ------------------------------------------------------ */
  Reels.prototype.open = function (index) {
    this.build();
    if (!this.built) return;

    var start = this.loop ? this.n + index : index;
    this.domIndex = start;
    this.active = index;

    /* teardown() pauses the active video, which legitimately badges it. Without
       this the next open paints a full-size play button over the reel until the
       play() promise resolves. */
    var stale = this.track.querySelectorAll('.hxp-reel.is-paused');
    for (var q = 0; q < stale.length; q++) stale[q].classList.remove('is-paused');

    this.lockScroll();
    this.modal.showModal();

    /* Layout has to exist before scrollTop means anything, hence the rAF. */
    var self = this;
    requestAnimationFrame(function () {
      self.jump(start * self.track.clientHeight);
      self.onActiveChange();
      if (self.opts.panelDefault) self.togglePanel(true);
      self.track.focus({ preventScroll: true });
    });

    /* Android's back gesture should dismiss the viewer, not leave the PDP. */
    if (this.opts.historyClose) {
      history.pushState({ hxpReels: true }, '');
      this.onPop = function () { if (self.modal.open) self.modal.close(); };
      window.addEventListener('popstate', this.onPop);
    }
  };

  Reels.prototype.closeViewer = function () {
    if (this.opts.historyClose && history.state && history.state.hxpReels) history.back();
    else this.modal.close();
  };

  Reels.prototype.teardown = function () {
    var self = this;
    Object.keys(this.videos).forEach(function (k) {
      var v = self.videos[k];
      v.pause();
      v.removeAttribute('src');
      while (v.firstChild) v.removeChild(v.firstChild);
      v.load();
      if (v.parentNode) v.parentNode.removeChild(v);
      delete self.videos[k];
    });
    this.togglePanel(false);
    this.unlockScroll();
    if (this.onPop) { window.removeEventListener('popstate', this.onPop); this.onPop = null; }
    /* Return focus to the card that opened the viewer — specifically the REAL
       one. With the rail loop armed the first match is a clone: aria-hidden,
       tabindex -1 and usually scrolled off screen, so focus would vanish into
       a node screen readers cannot see. */
    var card = this.rail &&
      this.rail.querySelector('[data-hxpr-card="' + this.active + '"]:not([aria-hidden])');
    if (card) card.focus({ preventScroll: true });
  };

  /* iOS ignores `overflow:hidden` on <body> once a scroll is in flight, so the
     position is pinned and restored by hand. */
  Reels.prototype.lockScroll = function () {
    this.scrollY = window.scrollY;
    document.documentElement.classList.add('hxp-reels-locked');
    document.body.style.position = 'fixed';
    document.body.style.top = -this.scrollY + 'px';
    document.body.style.width = '100%';
  };

  Reels.prototype.unlockScroll = function () {
    document.documentElement.classList.remove('hxp-reels-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.scrollY || 0);
  };

  Reels.prototype.destroy = function () {
    if (this.io) this.io.disconnect();
    if (this.onKey) document.removeEventListener('keydown', this.onKey);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onPop) window.removeEventListener('popstate', this.onPop);
    if (this.modal && this.modal.open) this.modal.close();
  };

  /* ---- boot -------------------------------------------------------------- */
  var instances = [];
  function init(scope) {
    (scope || document).querySelectorAll('[data-hxpr-root]').forEach(function (root) {
      if (root.__hxpr) return;
      root.__hxpr = new Reels(root);
      instances.push(root.__hxpr);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  /* Theme editor: sections are replaced wholesale, so rebind and release. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-hxpr-root]');
    if (root && root.__hxpr) { root.__hxpr.destroy(); root.__hxpr = null; }
  });
})();
